import React, { FC, useContext, useEffect } from "react";
import useSWR from "swr";

import { getActiveCourseEnrollments, getCourses } from "../../apis/get-courses";
import { getTasksAndServiceProspects } from "../../apis/get-tasks-and-services";
import { useLayout } from "../../hooks/use-layout";

import { Section } from "@in/component-library";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../app-context";
import { getLocalStorageItem } from "../../../utils/localstorage";
import { getApplications } from "../../apis/get-applications";
import { getHighlightedLinks } from "../../apis/get-highlighted-links";
import { getIsNewCustomer } from "../../apis/get-is-new-customer";
import { getServiceCategories } from "../../apis/get-service-categories";
import { getServiceMessages } from "../../apis/get-service-messages";
import CourseList from "../../components/course-list/course-list";
import HighlightedLinkList from "../../components/highlighted-link-list/highlighted-link-list";
import NewCustomerInfo from "../../components/new-customer-info/new-customer-info";
import OngoingCases from "../../components/ongoing-cases/ongoing-cases";
import Page from "../../components/page/page";
import ServiceCategoryList from "../../components/service-category-list/service-category-list";
import ServiceMessageList from "../../components/service-message-list/service-message-list";
import { Tasks } from "../../components/tasks/tasks";

const Home: FC = () => {
  const { translations } = useLayout();
  const { account, locale } = useContext(AppContext);
  const navigate = useNavigate();
  const { data: highlightedLinks, error: linksError } = useSWR([`highlighted-links`, locale, account], ([url]) =>
    getHighlightedLinks(url)
  );

  const { data: serviceCategories, error: categoriesError } = useSWR([`service-categories`, locale, account], ([url]) =>
    getServiceCategories(url)
  );

  const { data: courses, error: coursesError } = useSWR([`courses`, locale, account], ([url]) => getCourses(url));

  const {
    data: tasksAndServiceProspects,
    isLoading: tasksAndServiceProspectsIsLoading,
    error: tasksAndServiceProspectsError
  } = useSWR([`tasks/tasks-and-serviceprospects`, locale, account], ([url]) => getTasksAndServiceProspects(url));

  const { data: activeEnrollments, error: enrollmentsError } = useSWR(
    [`courses/enrollments/currentuser/active`, locale, account],
    ([url]) => getActiveCourseEnrollments(url)
  );

  const { data: serviceMessages, error: serviceMessagesError } = useSWR(
    [`service-messages`, locale, account],
    ([url]) => getServiceMessages(url)
  );

  const {
    data: applications,
    error: applicationsError,
    isLoading: applicationsIsLoading
  } = useSWR([`applications`, account], ([url]) => getApplications(url));

  const { data: isNewCustomer, error: isNewCustomerError } = useSWR([`accounts/isnew`], ([url]) =>
    getIsNewCustomer(url)
  );

  const beta = getLocalStorageItem("beta");
  useEffect(() => {
    if (beta === "true") {
      navigate("/beta");
    }
  }, []);

  useEffect(() => {
    if (translations.applicationTitle && translations.companyName) {
      document.title = `${translations.applicationTitle} - ${translations.companyName}`;
    }
  }, [translations]);

  return (
    <>
      <Page
        inputs={[
          isNewCustomerError ? {} : isNewCustomer,
          linksError ? {} : highlightedLinks,
          categoriesError ? {} : serviceCategories,
          enrollmentsError ? {} : activeEnrollments,
          coursesError ? {} : courses,
          serviceMessagesError ? {} : serviceMessages
        ]}
      >
        <Section collapse>
          {serviceMessages && serviceMessages.length > 0 && <ServiceMessageList messages={serviceMessages} />}
          <h1 className="data-hj-suppress">{account?.isPersonal ? account.name : account?.companyName}</h1>
        </Section>

        <NewCustomerInfo
          isNewCustomer={isNewCustomer && isNewCustomer}
          heading={translations.newCustomerInfoHeader}
          body={translations.newCustomerInfo}
          error={isNewCustomerError}
          errorMessage={translations.loadingFailed}
        />

        <OngoingCases
          applications={applications && applications}
          heading={translations.ongoingCases}
          error={applicationsError}
          errorMessage={translations.loadingFailed}
        />

        <Tasks
          applications={applications && applications}
          heading={translations.homeEnrollmentsHeading}
          tasksAndServiceProspects={tasksAndServiceProspects}
          error={tasksAndServiceProspectsError || applicationsError}
          errorMessage={translations.loadingFailed}
          isLoading={tasksAndServiceProspectsIsLoading ?? applicationsIsLoading}
        />

        <HighlightedLinkList
          heading={translations.homePageHeading}
          links={highlightedLinks}
          error={linksError}
          errorMessage={translations.loadingFailed}
        />
        {serviceCategories && serviceCategories.length > 0 && (
          <ServiceCategoryList
            heading={
              account?.isPersonal
                ? translations.homeServiceCategoriesHeadingPersonal
                : translations.homeServiceCategoriesHeading
            }
            serviceCategories={serviceCategories}
          />
        )}
        {!!courses && <CourseList heading={translations.homeCoursesHeading} courses={courses} />}
      </Page>
    </>
  );
};

export default Home;
